import React from "react";
import { IoWarningOutline } from "react-icons/io5";
import { MdSchedule } from "react-icons/md";

interface WarningModalProps {
  show: boolean;
  selectedCount: number;
  todoExtraction: boolean;
  onClose: () => void;
  onContinue: () => void;
}

const WarningModal: React.FC<WarningModalProps> = ({
  show,
  selectedCount,
  todoExtraction,
  onClose,
  onContinue,
}) => {
  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 transform transition-all animate-slideUp">
          <div className="flex flex-col items-center mb-6">
            <div className="relative mb-4">
              <div className="absolute inset-0 bg-amber-100 rounded-full animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-amber-400 to-orange-500 rounded-full p-4 shadow-lg">
                <IoWarningOutline className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 text-center">
              Processing All Notes
            </h2>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <MdSchedule className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-800 font-medium mb-2">
                  All {selectedCount} notes are selected
                </p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Since all notes are selected, the processing time may take a
                  while. The AI{" "}
                  {todoExtraction ? "todo extraction" : "summarization"} will
                  analyze all notes in detail.
                </p>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 text-sm mb-6">
            Do you still want to continue?
          </p>

          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
            >
              Cancel
            </button>
            <button
              onClick={onContinue}
              className="px-6 py-3 text-white bg-gradient-to-r from-[#083B3E] to-[#0a4d51] hover:from-[#0a4d51] hover:to-[#0c5f64] rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2"
            >
              <span>Continue</span>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default WarningModal;